import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import { getUserType } from './auth.store';
import { URLConfig } from '../Utils/features';

export type VehicleServiceKpi = {
  service_due_vehicles: number;
  service_ok_vehicles: number;
  service_overdue_vehicles: number;
  total_mileage_km: number;
};

export type OperationSummary = {
  locations: { cancelled: number; delivered: number; planned: number; skipped: number };
  routes: { completed: number; in_progress: number; planned: number; unplanned: number };
};

export interface AnalyticsState {
  analyticsData: any;
  analyticsRouteData: any;
  vehicleServiceKpi: VehicleServiceKpi | null;
  loading: boolean;
}

const initialState: AnalyticsState = {
  analyticsData: null,
  analyticsRouteData: null,
  vehicleServiceKpi: null,
  loading: false,
};

export const fetchAnalyticsData = createAsyncThunk<any, { username?: string; start_date?: string; end_date?: string }>(
  'routes/analytics',
  async ({ username, start_date, end_date }, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    const url = URLConfig.getKPI[userType];
    if (!url) return rejectWithValue('Not available for this user type');
    const params: Record<string, string> = {};
    if (userType === 'admin') {
      if (username) params.customerUsername = username;
    } else if (username) params.username = username;
    if (start_date) params.startDate = start_date;
    if (end_date) params.endDate = end_date;
    const [err, data] = await to(APIService.get(url, { params }));
    if (err) {
      toast.error('Analytics Data is not loaded. Please try again!');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const fetchAnalyticsRouteData = createAsyncThunk<any, { start_date?: string; end_date?: string }>(
  'routes/analyticsRoute',
  async ({ start_date, end_date }, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    const url = URLConfig.getKPIRouteOverview[userType];
    if (!url) return rejectWithValue('Not available for this user type');
    const params: Record<string, string> = {};
    if (start_date) params.startDate = start_date;
    if (end_date) params.endDate = end_date;
    const [err, data] = await to(APIService.get(url, { params }));
    if (err) {
      toast.error('Route Analytics Data is not loaded. Please try again!');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const fetchVehicleServiceKpi = createAsyncThunk<VehicleServiceKpi, void>(
  'routes/vehicleServiceKpi',
  async (_, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    const url = URLConfig.getKPIVehicleService[userType];
    if (!url) return rejectWithValue('Not available for this user type');
    const [err, data] = await to(APIService.get(url));
    if (err) {
      toast.error('Vehicle service analytics could not be loaded. Please try again!');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAnalyticsData.pending, (s) => {
      s.loading = true;
    });
    builder.addCase(fetchAnalyticsData.rejected, (s) => {
      s.loading = false;
    });
    builder.addCase(fetchAnalyticsData.fulfilled, (s, { payload }) => {
      s.loading = false;
      s.analyticsData = payload;
    });

    builder.addCase(fetchAnalyticsRouteData.pending, (s) => {
      s.loading = true;
    });
    builder.addCase(fetchAnalyticsRouteData.rejected, (s) => {
      s.loading = false;
    });
    builder.addCase(fetchAnalyticsRouteData.fulfilled, (s, { payload }) => {
      s.loading = false;
      s.analyticsRouteData = payload;
    });

    builder.addCase(fetchVehicleServiceKpi.fulfilled, (s, { payload }) => {
      s.vehicleServiceKpi = payload;
    });
  },
});

const analyticsStore = (store: RootState): AnalyticsState => store.analytics;
export const getAnalyticsLoading = createSelector(analyticsStore, (s) => s.loading);
export const getRoutesAnalyticsData = createSelector(analyticsStore, (s) => s.analyticsData);
export const getRoutesAnalyticsTableData = createSelector(analyticsStore, (s) => s.analyticsRouteData);
export const getVehicleServiceKpi = createSelector(analyticsStore, (s) => s.vehicleServiceKpi);

export default analyticsSlice.reducer;
